import React, {Suspense, useCallback, useEffect, useRef, useState, type ComponentRef, type RefObject} from 'react';
import type {MeshType} from 'api/generated';
import {Box3, Color, Vector3, WebGLRenderer, type Group} from 'three';
import {Canvas, useThree} from '@react-three/fiber';
import {OrbitControls, SoftShadows} from '@react-three/drei';
import {Spinner} from 'components';
import {classNames} from 'utils';
import {useDelay} from 'hooks';

import AssetGLB from './Model.GLB';
import {ArrowsPointingOutIcon} from '@heroicons/react/24/outline';

interface IModelViewerProps {
  glRef?: RefObject<WebGLRenderer | null>;
  mesh: MeshType;
  className?: string;
}

const ModelViewer = ({glRef, mesh, className = 'w-full aspect-square rounded-md overflow-hidden'}: IModelViewerProps) => {
  const ref = useRef<ComponentRef<'div'>>(null);
  const [meshSize, setMeshSize] = useState<Vector3>();

  const toggleFullscreen = () => {
    if (ref.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        ref.current.requestFullscreen();
      }
    }
  };

  const handleCalcSize = useCallback(
    (group: Group) => {
      const size = new Vector3();
      const boundingBox = new Box3().setFromObject(group);
      boundingBox.getSize(size);
      setMeshSize(size);
    },
    [setMeshSize],
  );

  const {isDelaying} = useDelay();
  if (isDelaying) {
    return (
      <div className={classNames(className, 'flex justify-center items-center')}>
        <Spinner />
      </div>
    );
  }

  return (
    <div ref={ref} className={classNames(className, 'relative')}>
      <Canvas
        shadows
        gl={{preserveDrawingBuffer: true}}
        scene={{background: new Color('#e2e8f1')}}
        camera={{position: [0, 4, 5], fov: 60, near: 0.1, far: 1000}}
        onCreated={({gl}) => glRef && (glRef.current = gl)}>
        <ambientLight intensity={2} />
        <directionalLight intensity={2} position={[20, 20, 20]} />
        <directionalLight intensity={2} position={[-20, 20, -20]} />
        <directionalLight
          intensity={2}
          position={[0, 10, 0]}
          castShadow
          shadow-mapSize-width={4096}
          shadow-mapSize-height={4096}
          shadow-camera-left={-10}
          shadow-camera-right={10}
          shadow-camera-top={10}
          shadow-camera-bottom={-10}
        />
        <Model key={mesh.uid} mesh={mesh} meshSize={meshSize} onLoad={handleCalcSize} />
        <SoftShadows />
        {!!meshSize && (
          <>
            <OrbitControls target={[0, meshSize.y * 0.5, 0]} enablePan enableZoom enableRotate minDistance={0.1} maxDistance={10} />
            <Options meshSize={meshSize} />
          </>
        )}
      </Canvas>
      <button
        type="button"
        className="absolute bottom-1.5 right-1.5 p-1 cursor-pointer bg-primary duration-200 rounded-md hover:opacity-75 active:scale-90"
        onClick={toggleFullscreen}>
        <ArrowsPointingOutIcon className="size-7 text-white" />
      </button>
    </div>
  );
};

export default React.memo(ModelViewer);

interface IModelProps {
  mesh: MeshType;
  meshSize: Vector3 | undefined;
  onLoad?: (group: Group) => void;
}

const Model = ({mesh, meshSize, onLoad}: IModelProps) => {
  const ref = useRef<Group>(null);
  const handleLoad = useCallback(() => {
    if (ref.current) {
      onLoad?.(ref.current);
    }
  }, [onLoad]);
  return (
    <Suspense fallback={null}>
      <group ref={ref} visible={!!meshSize}>
        {!!meshSize && <Shadow meshSize={meshSize} />}
        <mesh>
          {(() => {
            if (mesh.fileFormat === 'glb') {
              return <AssetGLB mesh={mesh} onLoad={handleLoad} />;
            }
          })()}
        </mesh>
      </group>
    </Suspense>
  );
};

const Shadow = ({meshSize}: {meshSize: Vector3}) => {
  return (
    <mesh receiveShadow scale={[meshSize.x * 1.25, meshSize.z * 1.25, 0.001]} rotation={[-Math.PI / 2, 0, 0]}>
      <shadowMaterial opacity={0.1} />
      <planeGeometry />
    </mesh>
  );
};

const Options = ({meshSize}: {meshSize: Vector3}) => {
  const {camera} = useThree();
  useEffect(() => {
    camera.position.set(0, meshSize.y, meshSize.z + meshSize.x * 0.5);
  }, [camera, meshSize]);
  return null;
};
